/**
 * 품목 선택 콤보 — 매입 등록·수정 창(PurchaseFormModal)에서 떼어 냄 (v2.304.0 · 개발요청 #16).
 *   판매물품 구입지급결의서의 줄마다 「매입 품목」을 고를 때도 같은 부품을 쓴다(ExpenseDocModal · ItemPickModal) —
 *   같은 품목 목록·같은 「+ 목록에 없음 — 직접 입력」 규칙이 두 벌로 갈리지 않게.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as stockApi from '../lib/stock-api';
import type { ItemMasterRow } from '../lib/types';

// ─────────────────────────────────────────────────────────────
// 품목 선택 콤보 (2026-05-14 신규 · 2026-05-18 직접입력 모드 추가)
//
// 검색: 품목명 / 규격 / 그룹 (백엔드 /stock/items?q= — P/N(품목코드)으로는 검색 안 함)
// 선택 시 itemCode + itemName 자동 채움.
//
// "+ 목록에 없음 — 직접 입력" — 드롭다운 맨 위 항목.
// 클릭 시 그룹·품명·규격 직접 입력 (item_master 에 자동 등록 + 추가입력 필요 마커).
// 코드는 백엔드가 자동 발급 (AUTO-…).
// ─────────────────────────────────────────────────────────────
export type PickerSelection = {
  code: string | null;             // null = 백엔드가 자동 코드 발급
  name: string;
  group?: string;
  spec?: string;
};

export function ItemPickerCombo({
  itemCode,
  itemName,
  onSelect,
  onClear,
}: {
  itemCode: string;
  itemName: string;
  onSelect: (sel: PickerSelection) => void;
  onClear: () => void;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  // 직접 입력 모드 ON 시 그룹/품명/규격 3 필드를 보여줌
  const [manualMode, setManualMode] = useState(false);
  const [manualGroup, setManualGroup] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualSpec, setManualSpec] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // 직접 입력 모드 진입 시 한 번 — 기존 item_master 의 distinct itemGroup 목록 조회.
  const groupsQ = useQuery({
    queryKey: ['item-groups-distinct'],
    queryFn: () => stockApi.listItems({}),
    enabled: manualMode,
    staleTime: 5 * 60_000,
  });
  const groupOptions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of groupsQ.data ?? []) {
      if (r.itemGroup) set.add(r.itemGroup);
    }
    // ETC 가 마스터에 없어도 항상 옵션으로 노출 (사용자 정책)
    set.add('ETC');
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [groupsQ.data]);

  // 외부 클릭 닫기
  useEffect(() => {
    if (!open && !manualMode) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        // 직접 입력 모드는 외부 클릭 시에도 유지 (실수 방지). [✕ 취소] 로만 종료.
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, manualMode]);

  const trimmed = search.trim();
  const q = useQuery({
    queryKey: ['items-picker', trimmed],
    queryFn: () => stockApi.listItems({ q: trimmed || undefined }),
    enabled: open,
    staleTime: 30_000,
  });
  // 후처리: 검색어와 정확히 일치하는 품목그룹이 있으면 그 그룹의 품목만 우선 표시.
  // (예: "HDD" 입력 → 그룹="HDD" 인 항목들만. "16TB" 입력 → 부분일치 결과 전체.)
  const { items, groupNarrowedTo } = useMemo<{
    items: ItemMasterRow[];
    groupNarrowedTo: string | null;
  }>(() => {
    const list = q.data ?? [];
    if (trimmed) {
      const t = trimmed.toLowerCase();
      const groupExact = list.filter(
        (it) => (it.itemGroup ?? '').toLowerCase() === t,
      );
      if (groupExact.length > 0) {
        return {
          items: groupExact.slice(0, 100),
          groupNarrowedTo: groupExact[0]!.itemGroup ?? null,
        };
      }
    }
    return { items: list.slice(0, 100), groupNarrowedTo: null };
  }, [q.data, trimmed]);

  // 이미 선택된 상태 (수정 모드 진입 시 또는 사용자가 고른 후)
  const hasSelection = !!itemCode;

  function commitManual() {
    const n = manualName.trim();
    const g = manualGroup.trim();
    if (!n || !g) return;
    onSelect({
      code: null,                 // 백엔드가 AUTO- 코드 자동 발급
      name: n,
      group: g,
      spec: manualSpec.trim() || undefined,
    });
    setManualMode(false);
    setManualGroup('');
    setManualName('');
    setManualSpec('');
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative mt-1">
      {hasSelection ? (
        <div className="flex items-center gap-2 border border-emerald-300 bg-emerald-50 rounded px-2 py-1.5">
          <div className="flex-1 min-w-0 text-sm">
            <span className="font-semibold text-emerald-800">{itemName || '(이름 없음)'}</span>
            <span className="ml-2 text-xs text-emerald-600 font-mono">{itemCode}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              onClear();
              setSearch('');
              setManualMode(false);
              setOpen(true);
            }}
            className="text-xs text-rose-600 hover:underline"
          >
            ✕ 변경
          </button>
        </div>
      ) : manualMode ? (
        <div className="border border-amber-300 bg-amber-50 rounded p-2 space-y-2">
          <div className="text-xs font-semibold text-amber-800">
            직접 입력 — 코드는 자동 발급(AUTO-…). 재고관리 → 품명&품목 에 ⚠ 추가입력 필요 로 표시됨
          </div>
          <div className="grid grid-cols-[160px_1fr] gap-1 items-center">
            <label className="ui-label text-amber-800">품목그룹</label>
            <select
              value={manualGroup}
              onChange={(e) => setManualGroup(e.target.value)}
              className="ui-input ui-input-sm border-amber-300"
              autoFocus
            >
              <option value="">선택하세요 (없으면 ETC)</option>
              {groupOptions.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <label className="ui-label text-amber-800">품명</label>
            <input
              type="text"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder="예: SM 16TB NL-SAS"
              className="ui-input ui-input-sm border-amber-300"
            />
            <label className="ui-label text-amber-800">규격</label>
            <input
              type="text"
              value={manualSpec}
              onChange={(e) => setManualSpec(e.target.value)}
              placeholder="(선택) 자세한 스펙 텍스트"
              className="ui-input ui-input-sm border-amber-300"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setManualMode(false);
                setManualGroup('');
                setManualName('');
                setManualSpec('');
              }}
              className="btn btn-ghost btn-sm"
            >
              ✕ 취소
            </button>
            <button
              type="button"
              onClick={commitManual}
              disabled={!manualName.trim() || !manualGroup.trim()}
              className="btn btn-warning-solid btn-sm"
            >
              ✓ 사용
            </button>
          </div>
        </div>
      ) : (
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="품명, 규격으로 검색 (예: SR650V4, 16TB)"
          className="ui-input w-full"
          autoComplete="off"
        />
      )}
      {open && !hasSelection && !manualMode && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-300 rounded shadow-lg z-30 max-h-72 overflow-y-auto">
          {/* 맨 위 — 목록에 없음 직접 입력. 항상 노출. */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setManualMode(true);
              setManualGroup('');
              setManualName(trimmed);    // 검색어를 품명 초기값으로
              setManualSpec('');
              setOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-xs border-b border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800 font-semibold"
          >
            + 목록에 없음 — 직접 입력
            {trimmed && <span className="ml-2 text-amber-600 font-mono font-normal">"{trimmed}"</span>}
          </button>
          {groupNarrowedTo && (
            <div className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 border-b border-blue-100">
              💡 품목그룹 <strong>"{groupNarrowedTo}"</strong> 으로 좁힘 ({items.length}건)
            </div>
          )}
          {q.isLoading && (
            <div className="px-3 py-2 text-xs text-slate-500">검색 중…</div>
          )}
          {!q.isLoading && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">
              {trimmed ? '검색 결과가 없습니다.' : '품목을 검색하세요.'}
            </div>
          )}
          {items.map((it) => (
            <button
              key={it.itemCode}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect({
                  code: it.itemCode,
                  name: it.itemName ?? '',
                  group: it.itemGroup ?? undefined,
                  spec: it.spec ?? undefined,
                });
                setSearch('');
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
            >
              <div className="font-semibold">
                {it.itemName ?? '-'}
                {it.spec && <span className="ml-2 text-slate-500 font-normal">{it.spec}</span>}
                {it.needsReview && (
                  <span className="ml-2 inline-block px-1 bg-amber-100 text-amber-700 rounded text-[11px]">⚠ 추가입력 필요</span>
                )}
              </div>
              {/* 품목 식별은 품목그룹·품목명·규격으로 — P/N(품목코드)은 표시하지 않음 */}
              {(it.itemGroup || it.isKeyStock) && (
                <div className="text-[11px] text-slate-500">
                  {it.itemGroup && <span className="text-slate-500">{it.itemGroup}</span>}
                  {it.isKeyStock && (
                    <span className="ml-2 inline-block px-1 bg-blue-100 text-blue-700 rounded">주요재고</span>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
